import { gatewayApiAgent } from 'common/api/apiAgents';
import {
  AssortmentProduct,
  AssortmentProductIdWrapper,
  AssortmentSet,
  AssortmentSetCreateRequest,
  AssortmentSetDetailsPage,
  AssortmentSetDetailsSearchRequest,
  AssortmentSetRemoveProductsRequest,
  AssortmentSetsSummarySearchRequest,
  AssortmentSetSummaryList,
  AssortmentSetUpdateRequest,
  BulkOperationAction,
  BulkOperationResponseV2,
  BulkOperationType,
  CatalogProductList,
  Category,
  CategoryCreateRequest,
  CategoryList,
  CategorySummaryListV3,
  CategoryUpdateRequest,
  DiscountRulesUpdateRequest,
  EditOffersBulkOperationCreateRequestV2,
  ExportAssortmentSetProductsBulkOperationCreateRequest,
  ExportBulkOperationCreateRequestV2,
  ExportOfferRevisionsBulkOperationCreateRequest,
  Offer,
  OfferCreateRequest,
  OfferList,
  OffersDeleteRequest,
  OffersSummarySearchRequest,
  OfferUpdateRequest,
  OfferVariantsUpdateRequest,
  ProductSummaryPage,
  PromotionsUpdateRequest,
  ReorderCategoriesRequestV2,
} from 'common/api-types/assortment';
import { CountryCode, Image } from 'common/api-types/common';

export async function fetchAssortmentProduct(assortmentProductId: string) {
  const response = await gatewayApiAgent.get<AssortmentProduct>(
    `/assortment/public/v1/assortment-products/${assortmentProductId}`,
  );
  return response.data;
}

export async function updateOfferVariants(
  assortmentProductId: string,
  request: OfferVariantsUpdateRequest,
) {
  await gatewayApiAgent.put(
    `/assortment/public/v1/assortment-products/${assortmentProductId}/offer-variants`,
    request,
  );
}

export type FetchCategoriesParams = {
  countryCode: CountryCode;
};

export async function fetchCategories(params: FetchCategoriesParams) {
  const response = await gatewayApiAgent.get<CategoryList>('/assortment/public/v1/categories', {
    params,
  });
  return response.data;
}

export const createCategory = async (request: CategoryCreateRequest): Promise<Category> => {
  const response = await gatewayApiAgent.post('/assortment/public/v1/categories', request);

  return response.data;
};

export const updateCategory = async (
  categoryId: string,
  request: CategoryUpdateRequest,
): Promise<Category> => {
  const response = await gatewayApiAgent.put(
    `/assortment/public/v1/categories/${categoryId}`,
    request,
  );

  return response.data;
};

export const fetchCategory = async (categoryId: string): Promise<Category> => {
  const response = await gatewayApiAgent.get(`/assortment/public/v1/categories/${categoryId}`);

  return response.data;
};

export const fetchAssortmentProductId = async (
  countryCode: string,
  categoryProductId: string,
): Promise<AssortmentProductIdWrapper> => {
  const response = await gatewayApiAgent.get(
    `/assortment/public/v1/assortment-products/${countryCode}/${categoryProductId}`,
  );

  return response.data;
};

export const deleteCategory = async (categoryId: string): Promise<void> => {
  const response = await gatewayApiAgent.delete(`/assortment/public/v1/categories/${categoryId}`);

  return response.data;
};

export type FetchCategoriesSummaryParamsV3 = {
  countryCode: CountryCode;
  q?: string;
};

export const fetchCategoriesSummaryV3 = async (params: FetchCategoriesSummaryParamsV3) => {
  const response = await gatewayApiAgent.get<CategorySummaryListV3>(
    '/assortment/public/v3/categories-summary',
    {
      params,
    },
  );

  return response.data;
};

export const updateCategoriesOrderV2 = async (request: ReorderCategoriesRequestV2) =>
  await gatewayApiAgent.post<void>('/assortment/public/v2/categories/reorder', request);

export const updateDiscountRules = async (
  assortmentProductId: string,
  request: DiscountRulesUpdateRequest,
) => {
  const response = await gatewayApiAgent.put<void>(
    `/assortment/public/v1/assortment-products/${assortmentProductId}/discount-rules`,
    request,
  );

  return response.data;
};

export type FetchCatalogProductsParams = {
  countryCode: CountryCode;
  q?: string;
};

export const fetchCatalogProducts = async (params: FetchCatalogProductsParams) => {
  const response = await gatewayApiAgent.get<CatalogProductList>(
    '/assortment/public/v1/catalog-products',
    {
      params,
    },
  );

  return response.data;
};

export const updatePromotions = async (
  assortmentProductId: string,
  request: PromotionsUpdateRequest,
) => {
  const response = await gatewayApiAgent.put(
    `/assortment/public/v1/assortment-products/${assortmentProductId}/promotions`,
    request,
  );

  return response.data;
};

export const endPromotion = async (assortmentProductId: string, promotionId: string) => {
  const response = await gatewayApiAgent.post(
    `/assortment/public/v1/assortment-products/${assortmentProductId}/promotions/${promotionId}/end`,
    {},
  );

  return response.data;
};

export const endAllActivePromotions = async (assortmentProductId: string) => {
  const response = await gatewayApiAgent.post<void>(
    `/assortment/public/v1/assortment-products/${assortmentProductId}/promotions/end-all-active`,
    {},
  );

  return response.data;
};

export const uploadImage = async (image: File) => {
  const response = await gatewayApiAgent.post<Image>('/assortment/public/v1/images', image, {
    headers: {
      'Content-Type': image.type,
    },
  });

  return response.data;
};

export type FetchBulkUploadTemplateParams = {
  bulkOperationType: BulkOperationType;
  countryCode: CountryCode;
  bulkOperationAction: BulkOperationAction;
};

export const fetchBulkUploadTemplate = async (params: FetchBulkUploadTemplateParams) => {
  const response = await gatewayApiAgent.get<string>(
    '/assortment/public/v1/bulk-operations/template',
    {
      params,
    },
  );

  return response.data;
};

export type UploadBulkFileParams = {
  countryCode: CountryCode;
  bulkOperationType: BulkOperationType;
  bulkOperationAction: BulkOperationAction;
};

export const uploadBulkImportFile = async (file: File, params: UploadBulkFileParams) => {
  const response = await gatewayApiAgent.post<BulkOperationResponseV2>(
    '/assortment/public/v2/bulk-operations/import',
    file,
    {
      params,
      headers: {
        'Content-Type': 'text/csv',
      },
    },
  );

  return response.data;
};

export const uploadBulkDeleteFile = async (file: File, params: UploadBulkFileParams) => {
  const response = await gatewayApiAgent.post<BulkOperationResponseV2>(
    '/assortment/public/v2/bulk-operations/delete',
    file,
    {
      params,
      headers: {
        'Content-Type': 'text/csv',
      },
    },
  );

  return response.data;
};

export const uploadBulkFile = async (file: File, params: UploadBulkFileParams) => {
  return params.bulkOperationAction === 'DELETE'
    ? uploadBulkDeleteFile(file, params)
    : uploadBulkImportFile(file, params);
};

export const downloadBulkFile = async (request: ExportBulkOperationCreateRequestV2) => {
  const response = await gatewayApiAgent.post<BulkOperationResponseV2>(
    '/assortment/public/v3/bulk-operations/export',
    request,
  );

  return response.data;
};

export const fetchBulkOperationStatus = async (bulkOperationId: string) => {
  const response = await gatewayApiAgent.get<BulkOperationResponseV2>(
    `/assortment/public/v2/bulk-operations/${bulkOperationId}`,
  );

  return response.data;
};

export const fetchBulkOperationReport = async (bulkOperationId: string) => {
  const response = await gatewayApiAgent.get<string>(
    `/assortment/public/v1/bulk-operations/${bulkOperationId}/report`,
  );

  return response.data;
};

export const fetchOffersSummary = async (request: OffersSummarySearchRequest) => {
  const response = await gatewayApiAgent.post<ProductSummaryPage>(
    '/assortment/public/v1/offers-summary/search',
    request,
  );

  return response.data;
};

export const createOffers = async (productId: string, request: OfferCreateRequest) => {
  const response = await gatewayApiAgent.post<void>(
    `/assortment/public/v1/${productId}/offers`,
    request,
  );

  return response.data;
};

export const fetchOffer = async (offerId: string) => {
  const response = await gatewayApiAgent.get<Offer>(`/assortment/public/v1/offers/${offerId}`);

  return response.data;
};

export const updateOffer = async (offerId: string, request: OfferUpdateRequest) => {
  const response = await gatewayApiAgent.put<Offer>(
    `/assortment/public/v1/offers/${offerId}`,
    request,
  );

  return response.data;
};

export type FetchOffersParams = {
  countryCode: CountryCode;
  productId: string;
};

export const fetchOffers = async (params: FetchOffersParams) => {
  const { countryCode, productId } = params;

  const response = await gatewayApiAgent.get<OfferList>(
    `/assortment/public/v1/${productId}/offers?countryCode=${countryCode}`,
  );

  return response.data;
};

export const deleteOffer = async (request: OffersDeleteRequest) => {
  const response = await gatewayApiAgent.post<void>(`/assortment/public/v1/offers/delete`, request);
  return response.data;
};

export const initiateOffersBulkEdit = async (request: EditOffersBulkOperationCreateRequestV2) => {
  const response = await gatewayApiAgent.post<BulkOperationResponseV2>(
    '/assortment/public/v3/bulk-operations/edit-offers',
    request,
  );

  return response.data;
};

export const initiateOfferRevisionsBulkDownload = async (
  request: ExportOfferRevisionsBulkOperationCreateRequest,
) => {
  const response = await gatewayApiAgent.post<BulkOperationResponseV2>(
    '/assortment/public/v2/bulk-operations/export-offer-revisions',
    request,
  );

  return response.data;
};

export const initiateAssortmentSetProductsBulkDownload = async (
  request: ExportAssortmentSetProductsBulkOperationCreateRequest,
) => {
  const response = await gatewayApiAgent.post<BulkOperationResponseV2>(
    '/assortment/public/v2/bulk-operations/export-assortment-set-products',
    request,
  );

  return response.data;
};

export const createAssortmentSet = async (request: AssortmentSetCreateRequest) => {
  const response = await gatewayApiAgent.post<AssortmentSet>(
    '/assortment/public/v1/assortment-sets',
    request,
  );

  return response.data;
};

export const fetchAssortmentSetsSummary = async (
  request: AssortmentSetsSummarySearchRequest,
): Promise<AssortmentSetSummaryList> => {
  const response = await gatewayApiAgent.post<AssortmentSetSummaryList>(
    '/assortment/public/v1/assortment-sets-summary/search',
    request,
  );

  return response.data;
};

export const fetchAssortmentSet = async (assortmentSetId: string): Promise<AssortmentSet> => {
  const response = await gatewayApiAgent.get<AssortmentSet>(
    `/assortment/public/v1/assortment-sets/${assortmentSetId}`,
  );

  return response.data;
};

export const updateAssortmentSet = async (
  assortmentSetId: string,
  request: AssortmentSetUpdateRequest,
): Promise<AssortmentSet> => {
  const response = await gatewayApiAgent.put<AssortmentSet>(
    `/assortment/public/v1/assortment-sets/${assortmentSetId}`,
    request,
  );

  return response.data;
};

export const deleteAssortmentSet = async (assortmentSetId: string) => {
  const response = await gatewayApiAgent.delete<void>(
    `/assortment/public/v1/assortment-sets/${assortmentSetId}`,
  );
  return response.data;
};

export const fetchAssortmentSetDetails = async (
  assortmentSetId: string,
  request: AssortmentSetDetailsSearchRequest,
): Promise<AssortmentSetDetailsPage> => {
  const response = await gatewayApiAgent.post<AssortmentSetDetailsPage>(
    `/assortment/public/v1/assortment-sets/${assortmentSetId}/details/search`,
    request,
  );

  return response.data;
};

export type AddAssortmentSetProductsRequest = {
  productIds: string[];
};

export const addProductsToAssortmentSet = async (
  assortmentSetId: string,
  request: AddAssortmentSetProductsRequest,
): Promise<void> => {
  const response = await gatewayApiAgent.post<void>(
    `/assortment/public/v1/assortment-sets/${assortmentSetId}/products/add`,
    request,
  );

  return response.data;
};

export const removeProductsFromAssortmentSet = async (
  assortmentSetId: string,
  request: AssortmentSetRemoveProductsRequest,
): Promise<void> => {
  const response = await gatewayApiAgent.post<void>(
    `/assortment/public/v1/assortment-sets/${assortmentSetId}/products/remove`,
    request,
  );

  return response.data;
};
